/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.4
 */

import { createElement } from 'react';

import type { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import { requestSaga } from '@/main/sagas/requestSaga';
import { API_HOSTNAME, TEMPORARY_PASSWORD } from '@modules/app/Api';
import { makeSelectTranslation } from '@modules/internationalization/intSelectors';
import type { ITranslation } from '@modules/internationalization/types';
import { modalOpen } from '@modules/modal/modalActions';
import type { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { openNotification } from '@modules/notification/notificationActions';
import { ENotificationLevelTypes } from '@modules/notification/types/notificationTypes';

import {
    getTemporaryPasswordFailure,
    getTemporaryPasswordSuccess,
    submitChangePasswordFormSuccess,
} from '../actions/changePasswordActions';
import ChangePasswordModal from '../components/ChangePassword/ChangePasswordModal';
import type { IChangePasswordModal } from '../components/ChangePassword/ChangePasswordModal';
import type { IChangePasswordFormPayload } from '../types/changePasswordTypes';

export function* changePasswordSaga(action: { payload: IChangePasswordFormPayload; type: string }): SagaIterator {
    const { password, passwordConfirm }: IChangePasswordFormPayload = action.payload;
    const { pageAuth }: ITranslation = yield select(makeSelectTranslation());
    const translation = yield select(makeSelectTranslation());

    try {
        const response = yield call(requestSaga, API_HOSTNAME + TEMPORARY_PASSWORD, 'POST', {
            confirmPassword: passwordConfirm,
            password: password,
        });
        yield put(submitChangePasswordFormSuccess(response));
        yield put(
            modalOpen({
                render: (props: IModalRenderProps) =>
                    createElement<IChangePasswordModal>(ChangePasswordModal, {
                        closeBtn: pageAuth.changePasswordForm.modal.button,
                        closeModal: props.onCloseModal,
                        isOpen: false,
                        text: pageAuth.changePasswordForm.modal.text,
                        title: pageAuth.changePasswordForm.modal.title,
                    }),
            }),
        );
    } catch (e) {
        const response = yield call((error) => error.response, e);
        yield put(
            openNotification({
                message: translation.common.error.codes[response.error.code] || translation.common.error.defaultMessage,
                level: ENotificationLevelTypes.ERROR,
            }),
        );
    }
}

export function* getTemporaryPasswordSaga(): SagaIterator {
    const translation = yield select(makeSelectTranslation());
    try {
        const response = yield call(requestSaga, API_HOSTNAME + TEMPORARY_PASSWORD, 'GET');
        yield put(getTemporaryPasswordSuccess(response));
    } catch (error: any) {
        yield put(getTemporaryPasswordFailure(error));
        yield put(
            openNotification({
                message: translation.common.error.defaultMessage,
                level: ENotificationLevelTypes.ERROR,
            }),
        );
    }
}
